"use client";

import Image from "next/image";
import React from "react";
import { toWebp } from "@/utils/common";
// import MobileSlider from "./slider";
import MobileSliderNew from "./MobileSliderNew";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

// Eyebrow label color cycles blue/green per card, matching the brand
// gradient pair (#0086E6 / #8ABC00) used for progress/accent bars elsewhere.
const LABEL_COLORS = ["#0086E6", "#8ABC00"];

interface TechnologyTeamProps {
  data?: any;
}

const TeamRow = ({ card, index }: { card: any; index: number }) => {
  const labelColor = LABEL_COLORS[index % LABEL_COLORS.length];

  const desktopUrl = card?.image?.data?.attributes?.url;
  const ipadUrl = card?.ipad_image?.data?.attributes?.url || desktopUrl;
  const hoverUrl = card?.hover_image?.data?.attributes?.url;

  const eyebrow = card?.title;
  const heading = card?.description;
  const paragraph = card?.description_2;

  return (
    <div className="flex flex-col items-center gap-[30px] py-[40px] first:pt-0 lg:flex-row  sm:py-[20px] md:py-[16px] xl:gap-[64px]  min-[1025px]:!py-[30px]">
      <div className="w-full text-center xl:w-[42%] lg:w-[50%] sm:text-left">
        {eyebrow && (
          <p
            className="mb-[8px] text-[16px] font-semibold leading-[24px] sm:text-[18px] lg:text-[20px]"
            style={{ color: labelColor }}
          >
            {eyebrow}
          </p>
        )}
        {heading && (
          <h3 className="mb-[16px] text-[24px] min-[1024px]:max-[1024px]:!text-[24px] min-[1025px]:!text-[32px] font-bold leading-[31px] text-white sm:leading-[36px] lg:leading-[42px] xl:leading-[42px]">
            {heading}
          </h3>
        )}
        {paragraph && (
          <p className="text-[14px] leading-[20px] text-white sm:text-[14px] lg:text-[16px] sm:leading-[20px] lg:leading-[24px]">
            {paragraph}
          </p>
        )}
      </div>

      <div className="group relative w-full xl:w-[58%] lg:w-[50%]">
        {/* Tablet illustration (sm up to just below desktop) */}
        {ipadUrl && (
          <div className="relative mx-auto h-[417px] w-[417px] lg:hidden">
            <Image
              src={toWebp(`${baseUrl}${ipadUrl}`)}
              alt={eyebrow || "illustration"}
              fill
              sizes="417px"
              className="object-contain"
            />
          </div>
        )}

        {/* Desktop illustration, with optional hover-swap artwork */}
        {desktopUrl && (
          <div className="relative ml-auto hidden xl:h-[531px] xl:w-[531px] w-[417px] h-[417px] lg:block">
            <Image
              src={toWebp(`${baseUrl}${desktopUrl}`)}
              alt={eyebrow || "illustration"}
              fill
              sizes="531px"
              className={
                hoverUrl
                  ? "object-contain transition-opacity duration-300 group-hover:opacity-0"
                  : "object-contain"
              }
              unoptimized
            />
            {hoverUrl && (
              <Image
                src={toWebp(`${baseUrl}${hoverUrl}`)}
                alt={eyebrow || "illustration"}
                fill
                sizes="531px"
                className="object-contain opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TechnologyTeam = ({ data }: TechnologyTeamProps) => {
  const cards = data?.cards || [];

  if (!cards.length) return null;

  return (
    <div className="relative overflow-hidden bg-background-dark text-primary-text">
      {/* Decorative corner vectors — a distinct asset pair per breakpoint */}
      <Image
        src="/images/clutch-landing/Blue-vector-mobile.png"
        width={430}
        height={669}
        alt="banner-vector"
        className="absolute top-0 left-0 z-1 block h-[669px] w-[430px] bg-cover bg-center bg-no-repeat sm:hidden"
        quality={80}
        loading="eager"
        priority
      />
      <Image
        src="/images/clutch-landing/Blue-vector-ipad.png"
        width={350}
        height={405}
        alt="banner-vector"
        className="absolute top-0 left-0 z-1 hidden h-[405px] w-[350px] bg-cover bg-center bg-no-repeat sm:block lg:hidden"
        quality={80}
        loading="eager"
        priority
      />
      <Image
        src="/images/clutch-landing/Green-vector-ipad.png"
        width={400}
        height={334}
        alt="banner-vector"
        className="absolute bottom-0 right-0 z-1 hidden h-[334px] w-[400px] bg-cover bg-center bg-no-repeat sm:block lg:hidden"
        quality={80}
        loading="eager"
        priority
      />
      <Image
        src="/images/clutch-landing/Blue-vector.png"
        width={500}
        height={578}
        alt="banner-vector"
        className="absolute top-0 left-0 z-1 hidden h-[578px] w-[500px] bg-cover bg-center bg-no-repeat lg:block"
        quality={80}
        loading="eager"
        priority
      />
      <Image
        src="/images/clutch-landing/Green-vector.png"
        width={600}
        height={391}
        alt="banner-vector"
        className="absolute bottom-0 right-0 z-1 hidden h-[391px] w-[600px] bg-cover bg-center bg-no-repeat lg:block"
        quality={80}
        loading="eager"
        priority
      />

      {/* Heading */}
      <div className="relative pb-0 pt-[56px] md:pb-0 md:pt-[80px] min-[1024px]:max-[1024px]:!pt-[80px] min-[1025px]:pt-[100px] xl:pb-0">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-white text-center xl:text-[48px] xl:leading-[62.4px] lg:!leading-[62px] text-[30px] lg:text-[40px] lg:font-semibold min-[1024px]:max-[1024px]:!text-[30px] min-[1025px]:text-[40px] tracking-[1px] font-bold max-w-[930px] mx-auto w-full mx-auto [@media_(min-width:1024px)_and_(max-width:1024px)]:!max-w-[100%] leading-[1.3]">
            {data?.title}
          </h2>
        </div>
      </div>

      <section className="relative overflow-hidden bg-background-dark pt-[32px] pb-[56px] md:pt-[30px] min-[1024px]:max-[1024px]:!pt-[40px] min-[1025px]:!pt-[30px] text-primary-text sm:pb-[40px] md:pb-[64px] min-[1025px]:!pb-[70px] xl:max-w-[1172px] max-w-[912px] w-full p-[0_24px] mx-auto">
        {/* Static stacked layout — identical on ipad/tablet and desktop */}
        <div className=" !px-[0] container relative z-10 mx-auto hidden  lg:block md:max-w-full ">
          {cards.map((card: any, index: number) => (
            <TeamRow key={card.id ?? index} card={card} index={index} />
          ))}
        </div>

        {/* One-directional, looping slider — mobile only */}
        <div className="container relative z-10 mx-auto px-0-custom lg:hidden">
          {/* <MobileSlider cards={cards} /> */}
          <MobileSliderNew cards={cards} />
        </div>
      </section>
    </div>
  );
};

export default TechnologyTeam;
